import React, { useEffect, useMemo } from 'react';
import { Input, Select, Typography, DatePicker, Radio } from 'antd';
// Constants
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_SUGGESTED_CAPTION_LENGTH,
  MAX_TITLE_LENGTH,
} from '../TimelineDeliverablesModal';
// Helpers
import {
  TimelineDeliverableFormData,
  convertToMoment,
  convertToDate,
  getTitlePlaceholder,
  getDueDateDisplayValue,
  getDueDateOptions,
  getApprovalUnitOptions,
  getSingleDateTypeOptions,
  getPostTaskOptions,
  getDisabledDate,
  createNumberOptions,
} from 'helpers/timelineDeliverables';
import { ValueLabelOption } from 'helpers/campaignEditModal';
// Types
import {
  CampaignSmPlatform,
  CampaignTaskType,
  CbDueDateType,
  CbDisplayPeriod,
  CbFixedDateType,
} from 'api/graphql-global-types';
// Utils
import {
  getCustomPostTypeOptions,
  SOCIAL_DELIVERABLE_PLATFORMS,
} from 'helpers/customCampaignConstants';
// Styles
import styles from './PostDetails.module.scss';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const PLATFORM_LABELS: Record<string, string> = {
  [CampaignSmPlatform.IG]: 'Instagram',
  [CampaignSmPlatform.TT]: 'TikTok',
};

type PostDetailsProps = {
  formData: TimelineDeliverableFormData;
  updateNestedFormData: (path: string, value: any) => void;
  platform: CampaignSmPlatform;
  /** Custom campaigns: platforms the admin can pick from in this modal. */
  availablePlatforms?: CampaignSmPlatform[];
  onPlatformChange?: (platform: CampaignSmPlatform) => void;
};

const PostDetails: React.FC<PostDetailsProps> = ({
  formData,
  updateNestedFormData,
  platform,
  availablePlatforms,
  onPlatformChange,
}) => {
  const dueDateType = formData.postDetails?.dueDateType;
  const dateRangeType = formData.postDetails?.dateRangeType;
  const approvalUnit = formData.postDetails?.approvalUnit;

  // When a platform selector is shown (custom campaigns), use the full
  // per-platform post-type lists (IG: Live/Post/Reel/Story, TT: Live/Post/Story).
  const showPlatformSelector =
    !!onPlatformChange && !!availablePlatforms && availablePlatforms.length > 0;

  const platformOptions = useMemo(
    () =>
      (availablePlatforms ?? [])
        .filter((p) => SOCIAL_DELIVERABLE_PLATFORMS.includes(p))
        .map((p) => ({ label: PLATFORM_LABELS[p] ?? p, value: p })),
    [availablePlatforms]
  );

  // Memoized options
  const dueDateOptions = useMemo(() => getDueDateOptions(), []);
  const approvalUnitOptions = useMemo(() => getApprovalUnitOptions(), []);
  const singleDateTypeOptions = useMemo(() => getSingleDateTypeOptions(), []);
  const postTaskOptions = useMemo(
    () =>
      showPlatformSelector
        ? getCustomPostTypeOptions(platform)
        : getPostTaskOptions(platform),
    [platform, showPlatformSelector]
  );

  // Keep the post type valid for the active platform's option list.
  useEffect(() => {
    const currentType = formData.postDetails?.postType;
    if (
      postTaskOptions.length > 0 &&
      !postTaskOptions.some((opt) => opt.value === currentType)
    ) {
      updateNestedFormData(
        'postDetails.postType',
        postTaskOptions[0].value as CampaignTaskType
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postTaskOptions]);

  // Computed values
  const isContentBased = dueDateType === CbDueDateType.CONTENT_BASED;
  const isFixed = dateRangeType === CbDueDateType.FIXED;
  const isRange = dateRangeType === CbDueDateType.RANGE;
  const isSpecific = dueDateType === 'specific';

  // Event handlers
  const handleDueDateChange = (option: ValueLabelOption) => {
    if (option.value === 'specific') {
      updateNestedFormData('postDetails.dueDateType', 'specific');
      if (!dateRangeType) {
        updateNestedFormData('postDetails.dateRangeType', CbDueDateType.FIXED);
        updateNestedFormData(
          'postDetails.singleDateType',
          CbFixedDateType.EXACT
        );
      }
    } else {
      updateNestedFormData('postDetails.dueDateType', option.value);
    }
  };

  const handleApprovalUnitChange = (value: CbDisplayPeriod) => {
    updateNestedFormData('postDetails.approvalUnit', value);
    updateNestedFormData('postDetails.approvalNumber', undefined);
  };

  const handleApprovalNumberChange = (option: string) => {
    updateNestedFormData('postDetails.approvalNumber', Number(option));
  };

  const handleDateRangeTypeChange = (value: CbDueDateType) => {
    updateNestedFormData('postDetails.dateRangeType', value);
    if (value === CbDueDateType.FIXED) {
      updateNestedFormData('postDetails.singleDateType', CbFixedDateType.EXACT);
      updateNestedFormData('postDetails.startDate', undefined);
      updateNestedFormData('postDetails.endDate', undefined);
    } else if (value === CbDueDateType.RANGE) {
      updateNestedFormData('postDetails.dueDate', undefined);
      updateNestedFormData('postDetails.singleDateType', undefined);
    }
  };

  return (
    <div className={styles.postDetails}>
      <Title level={3}>Post Details</Title>

      <div className={styles.helperText}>
        Add details for your post, including post type, suggested captions, and
        any additional instructions for talent to complete their post after
        content is approved.
      </div>

      {showPlatformSelector && (
        <div className={styles.formGroup}>
          <div>Platform</div>
          <Select
            value={platform}
            onChange={(value) =>
              onPlatformChange?.(value as CampaignSmPlatform)
            }
            placeholder="Select Platform"
            style={{ width: '100%' }}
          >
            {platformOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
      )}

      <div className={styles.formGroup}>
        <div>Post Type</div>
        <Select
          value={formData.postDetails?.postType}
          onChange={(value) =>
            updateNestedFormData('postDetails.postType', value)
          }
          placeholder="Select Post Type"
          style={{ width: '100%' }}
        >
          {postTaskOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      <div className={styles.formGroup}>
        <div>Post Title</div>
        <Input
          value={formData.postDetails?.postTitle || ''}
          onChange={(e) =>
            updateNestedFormData('postDetails.postTitle', e.target.value)
          }
          placeholder={getTitlePlaceholder(platform)}
          maxLength={MAX_TITLE_LENGTH}
        />
      </div>

      <div className={styles.formGroup}>
        <div>Description</div>
        <TextArea
          value={formData.postDetails?.description || ''}
          onChange={(e) =>
            updateNestedFormData('postDetails.description', e.target.value)
          }
          rows={4}
          placeholder="Include required or suggested copy, hashtags, @mentions, and whether to use a collaborator tag. Note how long the post should stay live, if it can be deleted, if it must be exclusive, and whether links are needed (e.g., in bio or swipe-up). Clear direction helps ensure the post meets your goals."
          maxLength={MAX_DESCRIPTION_LENGTH}
        />
        <div className={styles.helperText}>
          Describe what you expect talent to do for this post. Include specific
          instructions such as links, profile mentions, hashtags, and which
          content pieces to use (and in what order, if applicable).
        </div>
      </div>

      <div className={styles.formGroup}>
        <div>Suggested Caption</div>
        <TextArea
          value={formData.postDetails?.suggestedCaption || ''}
          onChange={(e) =>
            updateNestedFormData('postDetails.suggestedCaption', e.target.value)
          }
          rows={3}
          placeholder="Enter suggested caption"
          maxLength={MAX_SUGGESTED_CAPTION_LENGTH}
        />
        <div className={styles.helperText}>
          Add a suggested caption with hashtags, profile mentions, and talking
          points. You can provide a full caption or let talent personalize it —
          just make sure to clearly highlight anything that&apos;s required.
        </div>
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <div>Hashtags</div>
          <Input
            value={formData.postDetails?.hashtags || ''}
            onChange={(e) =>
              updateNestedFormData('postDetails.hashtags', e.target.value)
            }
            placeholder="e.g. #brand #MILLIONSad"
          />
        </div>

        <div className={styles.formGroup}>
          <div>Mentions</div>
          <Input
            value={formData.postDetails?.mentions || ''}
            onChange={(e) =>
              updateNestedFormData('postDetails.mentions', e.target.value)
            }
            placeholder="e.g. @mentionHandle"
          />
        </div>
      </div>

      <div className={styles.formGroup}>
        <div>Post Due Date</div>
        <Select
          placeholder="Select Post Deadline"
          value={getDueDateDisplayValue(dueDateType)}
          onChange={(optionValue: string) => {
            const option = dueDateOptions.find(
              (opt) => opt.value === optionValue
            );
            if (option) {
              handleDueDateChange(option);
            }
          }}
          style={{ width: '100%' }}
        >
          {dueDateOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>

      {isContentBased && (
        <div className={styles.rowWrapper}>
          <div className={styles.formGroup}>
            <div>Unit</div>
            <Select
              value={formData.postDetails?.approvalUnit}
              onChange={handleApprovalUnitChange}
              placeholder="Select unit"
              style={{ width: '200px' }}
              allowClear
            >
              {approvalUnitOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className={styles.formGroup}>
            <div>Number</div>
            <Select
              value={formData.postDetails?.approvalNumber?.toString()}
              onChange={(option: string) => handleApprovalNumberChange(option)}
              placeholder="Select number"
              style={{ width: '200px' }}
            >
              {createNumberOptions(
                approvalUnit === CbDisplayPeriod.DAYS ? 6 : 4
              ).map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {isSpecific && (
        <>
          <div className={styles.formGroup}>
            <div>Date Type</div>
            <Radio.Group
              value={dateRangeType}
              onChange={(e) => handleDateRangeTypeChange(e.target.value)}
            >
              <Radio value={CbDueDateType.FIXED}>Fixed Date</Radio>
              <Radio value={CbDueDateType.RANGE}>Range</Radio>
            </Radio.Group>
          </div>

          {isFixed && (
            <div className={styles.rowWrapper}>
              <div className={styles.formGroup}>
                <div>Timing</div>
                <Select
                  value={formData.postDetails?.singleDateType ?? undefined}
                  onChange={(value: CbFixedDateType) =>
                    updateNestedFormData('postDetails.singleDateType', value)
                  }
                  placeholder="Select timing"
                  style={{ width: '200px' }}
                >
                  {singleDateTypeOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className={styles.formGroup}>
                <div>Date</div>
                <DatePicker
                  value={convertToMoment(formData.postDetails?.dueDate)}
                  onChange={(date) =>
                    updateNestedFormData(
                      'postDetails.dueDate',
                      convertToDate(date)
                    )
                  }
                  placeholder="MM/DD/YYYY"
                  style={{ width: '200px' }}
                  disabledDate={getDisabledDate}
                />
              </div>
            </div>
          )}

          {isRange && (
            <div className={styles.rowWrapper}>
              <div className={styles.formGroup}>
                <div>Start Date</div>
                <DatePicker
                  value={convertToMoment(formData.postDetails?.startDate)}
                  onChange={(date) =>
                    updateNestedFormData(
                      'postDetails.startDate',
                      convertToDate(date)
                    )
                  }
                  placeholder="Start Date"
                  style={{ width: '200px' }}
                  disabledDate={getDisabledDate}
                />
              </div>
              <div className={styles.formGroup}>
                <div>End Date</div>
                <DatePicker
                  value={convertToMoment(formData.postDetails?.endDate)}
                  onChange={(date) =>
                    updateNestedFormData(
                      'postDetails.endDate',
                      convertToDate(date)
                    )
                  }
                  placeholder="End Date"
                  style={{ width: '200px' }}
                  disabledDate={getDisabledDate}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PostDetails;
